// client/src/hooks/issueTriage/useIssueTriageFlags.ts
import { useState, useEffect } from 'react';
import { getRemoteConfig, fetchAndActivate, getValue } from 'firebase/remote-config';
import type { IssueTriageFeatureFlags } from '../../types/issue-triage';
import { DEFAULT_ISSUE_TRIAGE_FLAGS } from '../../types/issue-triage';

/**
 * One retry after a short delay. Remote Config's IndexedDB layer can lose its
 * connection during page startup churn ("The connection was closed",
 * remoteconfig/storage-open — OFFICEHUB-CLIENT-5A); by the time the retry
 * runs the database is reopenable and the fetch succeeds. A second failure
 * means storage is genuinely unavailable (private mode, evicted quota) and
 * retrying further would not change the answer — defaults are used.
 */
const RETRY_DELAY_MS = 2_000;

/**
 * Hook to get Issue Triage feature flags from Firebase Remote Config
 *
 * @example
 * ```tsx
 * const flags = useIssueTriageFlags();
 * if (flags.contextCaptureEnabled) {
 *   // capture context
 * }
 * ```
 *
 * @returns Issue triage feature flags (defaults if Remote Config unavailable)
 */
export function useIssueTriageFlags(): IssueTriageFeatureFlags {
  const [flags, setFlags] = useState<IssueTriageFeatureFlags>(DEFAULT_ISSUE_TRIAGE_FLAGS);

  useEffect(() => {
    let cancelled = false;

    async function loadFlags(attempt: number): Promise<void> {
      try {
        const remoteConfig = getRemoteConfig();

        // Set defaults
        remoteConfig.defaultConfig = {
          issue_triage_context_capture_enabled: DEFAULT_ISSUE_TRIAGE_FLAGS.contextCaptureEnabled,
          issue_triage_auto_analysis_enabled: DEFAULT_ISSUE_TRIAGE_FLAGS.autoAnalysisEnabled,
          issue_triage_max_issues_per_hour: DEFAULT_ISSUE_TRIAGE_FLAGS.maxIssuesPerHour,
        };

        // Fetch and activate
        await fetchAndActivate(remoteConfig);
        if (cancelled) return;

        // Read values
        setFlags({
          contextCaptureEnabled: getValue(remoteConfig, 'issue_triage_context_capture_enabled').asBoolean(),
          autoAnalysisEnabled: getValue(remoteConfig, 'issue_triage_auto_analysis_enabled').asBoolean(),
          maxIssuesPerHour: getValue(remoteConfig, 'issue_triage_max_issues_per_hour').asNumber(),
        });
      } catch (error) {
        if (cancelled) return;
        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
          if (!cancelled) await loadFlags(1);
          return;
        }
        // The warn-downgrade is scoped to the LOCAL IndexedDB storage layer
        // (remoteconfig/storage-*): defaults are the designed fallback there,
        // so it is a degraded-but-fine state, and console.warn keeps it out of
        // the error-reporting bridge (OFFICEHUB-CLIENT-5A; the SDK's own
        // internal rejection for the same failure is filtered in index.tsx).
        // Every OTHER failure class — a real Remote Config outage, an auth or
        // quota error — keeps console.error, because losing that signal would
        // hide a genuine service fault behind the fallback.
        const code = String((error as { code?: unknown } | null | undefined)?.code ?? '');
        if (code.startsWith('remoteconfig/storage-')) {
          console.warn('[IssueTriageFlags] Failed to load flags, using defaults:', error);
        } else {
          console.error('[IssueTriageFlags] Failed to load flags:', error);
        }
      }
    }

    void loadFlags(0);
    return () => { cancelled = true; };
  }, []);

  return flags;
}
